/**
 * Material Service
 * All material/chart-related API endpoints
 */

import apiClient from './api/apiClient';
import { ENDPOINTS, buildEndpoint } from './api/endpoints';

/**
 * Material Service Object
 * Contains all methods for fetching material data
 */
export const materialService = {
  /**
   * 0. Get All Materials List
   * Endpoint: /materials
   *
   * Returns: Array<{
   *   id: number,
   *   code: string,
   *   name: string,
   *   description: string,
   *   last_value: number,
   *   value_unit: string,
   *   currency: string,
   *   last_date: string (ISO format),
   *   recent_chart_data: Array<{
   *     date: string,
   *     value: number,
   *     high: number,
   *     low: number,
   *     currency: string,
   *     unit: string
   *   }>
   * }>
   */
  getAllMaterials: async () => {
    return apiClient.get(ENDPOINTS.MATERIALS.LIST);
  },

  getAllMaterialsSlim: async () => {
    return apiClient.get(`${ENDPOINTS.MATERIALS.LIST}?slim=1`);
  },

  /**
   * 0b. Get lightweight materials list for compare dropdowns
   * Endpoint: /materials/compare/options
   */
  getCompareOptions: async () => {
    return apiClient.get(ENDPOINTS.MATERIALS.COMPARE_OPTIONS);
  },

  /**
   * Get All Sectors
   * Endpoint: /materials/sectors
   *
   * Returns: Array<{
   *   id: number,
   *   name: string,
   *   image: string | null
   * }>
   */
  getSectors: async () => {
    return apiClient.get(ENDPOINTS.MATERIALS.SECTORS);
  },

  /**
   * Get Top Sectors for Current User
   * Endpoint: /materials/me/top_sectors
   *
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of sectors to return (default 4, max 10)
   *
   * Returns: Array<{
   *   sector_id: number,
   *   name: string,
   *   image: string,
   *   score: number
   * }>
   */
  getTopSectors: async (params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.MATERIALS.TOP_SECTORS,
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Get Recently Viewed Materials for Current User
   * Endpoint: /materials/me/recent_views
   *
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of materials to return (optional)
   *
   * Returns: Array<{
   *   resource_type: string,
   *   id: number,
   *   code: string,
   *   name: string,
   *   viewed_at: string (ISO timestamp),
   *   time_series: Array<{ year: number, value: number, unit: string }>
   * }>
   */
  getRecentViews: async (params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.MATERIALS.RECENT_VIEWS,
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * 1. Get General Material Information
   * Endpoint: /materials/{material_code}/general
   *
   * Returns: {
   *   name: string,
   *   description: string,
   *   need_narrative: string,
   *   vat_percent: number,
   *   last_price_display: string,
   *   last_price_value: number,
   *   currency: string,
   *   value_unit: string,
   *   last_price_date_display: string,
   *   last_price_date_iso: string,
   *   change_display: string | null,
   *   change_abs_value: number | null,
   *   change_pct_value: number | null
   * }
   */
  getGeneral: async (materialCode) => {
    return apiClient.get(ENDPOINTS.MATERIALS.GENERAL(materialCode));
  },

  /**
   * 2. Get Chart Overview (6 years of historical data)
   * Endpoint: /materials/{material_code}/chart/overview
   *
   * Returns: Array<{
   *   date: string,
   *   monthLabel: string,
   *   value: number,
   *   timestamp: number
   * }>
   */
  getChartOverview: async (materialCode) => {
    return apiClient.get(ENDPOINTS.MATERIALS.CHART_OVERVIEW(materialCode));
  },

  /**
   * 3. Get Chart Series with Timeframe
   * Endpoint: /materials/{material_code}/chart/series?start={date}&end={date}&tf={timeframe}
   *
   * @param {string} materialCode - Material code (e.g., 'MAT_7')
   * @param {Object} params - Query parameters
   * @param {string} params.start - Start date (ISO format: YYYY-MM-DD)
   * @param {string} params.end - End date (ISO format: YYYY-MM-DD)
   * @param {string} params.tf - Timeframe (1h, 3h, 1d, 1w, 1m, 3m, 1y, 5y, max)
   * @param {string} [params.currency] - Zielwaehrung (Umrechnung im Backend mit Tageskursen)
   * @param {string} [params.unit] - Zieleinheit (nur innerhalb derselben Dimension)
   *
   * Returns: {
   *   meta: { source_currency, source_unit, currency, unit, converted, currency_converted,
   *           unit_converted, fx_basis, fx_series, points_without_rate, reason },
   *   data: Array<{
   *     date: string,
   *     xAxisLabel: string,
   *     value: number,
   *     open: number,
   *     high: number,
   *     low: number,
   *     close: number,
   *     volume: number,
   *     volumeNormalized: number
   *   }>
   * }
   */
  getChartSeries: async (materialCode, params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.MATERIALS.CHART_SERIES(materialCode),
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Get Chart Comparison - Compare multiple materials over the same time window
   * Endpoint: /materials/{material_code}/chart/compare?codes={codes}&start={date}&end={date}&tf={timeframe}
   *
   * @param {string} materialCode - Base material code (e.g., 'MAT_7')
   * @param {Object} params - Query parameters
   * @param {string} params.codes - Comma-separated list of material codes to compare (e.g., 'MAT_1,MAT_2')
   * @param {string} params.start - Start date (ISO datetime: YYYY-MM-DDTHH:mm:ssZ)
   * @param {string} params.end - End date (ISO datetime: YYYY-MM-DDTHH:mm:ssZ)
   * @param {string} params.tf - Timeframe (1h, 3h, 1d, 1w, 1m, 3m, 1y, 5y, max)
   * @param {string} [params.currency] - Zielwaehrung fuer alle verglichenen Reihen
   * @param {string} [params.unit] - Zieleinheit fuer alle verglichenen Reihen
   *
   * Returns: {
   *   meta: { [materialCode]: { ...wie chart/series meta } },
   *   series: {
   *     [materialCode]: Array<{
   *       date: string,
   *       xAxisLabel: string,
   *       open: number,
   *       high: number,
   *       low: number,
   *       close: number,
   *       value: number,
   *       volume: number
   *     }>
   *   }
   * }
   */
  getChartComparison: async (materialCode, params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.MATERIALS.CHART_COMPARE(materialCode),
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Export chart/price data for a material
   * Endpoint: /materials/{material_code}/chart/export
   *
   * @param {string} materialCode - Material code
   * @param {Object} params - Query parameters (start, end, tf, frequency, sort, include_forecast, include_news, currency, unit)
   * @returns {Promise<Object>} - Export payload containing data[], metadata, and optional extras
   */
  exportChart: async (materialCode, params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.MATERIALS.CHART_EXPORT(materialCode),
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Waehrungen fuer die Chart-Umrechnung
   * Endpoint: /reference/currencies
   */
  getReferenceCurrencies: async () => {
    return apiClient.get(ENDPOINTS.REFERENCE.CURRENCIES);
  },

  /**
   * Einheiten fuer die Chart-Umrechnung
   * Endpoint: /reference/units
   */
  getReferenceUnits: async () => {
    return apiClient.get(ENDPOINTS.REFERENCE.UNITS);
  },

  /**
   * 4. Get Assessment Metrics
   * Endpoint: /materials/{material_code}/assessment
   *
   * Returns: {
   *   material_code: string,
   *   commodity_necessity_value: number,
   *   commodity_necessity_description: string,
   *   risk_assessment_value: number,
   *   risk_assessment_description: string,
   *   recycling_rate_value: number,
   *   recycling_rate_description: string
   * }
   */
  getAssessment: async (materialCode) => {
    return apiClient.get(ENDPOINTS.MATERIALS.ASSESSMENT(materialCode));
  },

  /**
   * 5. Get Availability Data
   * Endpoint: /materials/{material_code}/availability
   *
   * Returns: {
   *   data: Array<{
   *     year: string,
   *     on: number,
   *     off: number
   *   }>,
   *   onValue: string,
   *   offValue: string,
   *   description: string
   * }
   */
  getAvailability: async (materialCode) => {
    return apiClient.get(ENDPOINTS.MATERIALS.AVAILABILITY(materialCode));
  },

  /**
   * 6. Get Country Production Distribution
   * Endpoint: /materials/{material_code}/country_production
   *
   * Returns: {
   *   countries: Array<{
   *     country: string,
   *     name: string,
   *     share: number,
   *     value: number,
   *     change: number
   *   }>
   * }
   */
  getCountryProduction: async (materialCode) => {
    return apiClient.get(ENDPOINTS.MATERIALS.COUNTRY_PRODUCTION(materialCode));
  },

  /**
   * 7. Get Market Stability by Country
   * Endpoint: /materials/{material_code}/market_stability
   *
   * Returns: Array<{
   *   country: string,
   *   stability: number,
   *   stability_description: string
   * }>
   */
  getMarketStability: async (materialCode) => {
    return apiClient.get(ENDPOINTS.MATERIALS.MARKET_STABILITY(materialCode));
  },

  /**
   * 8. Get Need Projection (10-year forecast)
   * Endpoint: /materials/{material_code}/need_projection
   *
   * Returns: {
   *   data: Array<{
   *     year: string,
   *     lowEstimate: number,
   *     averageEst: number,
   *     highEstimate: number
   *   }>,
   *   description: string
   * }
   */
  getNeedProjection: async (materialCode) => {
    return apiClient.get(ENDPOINTS.MATERIALS.NEED_PROJECTION(materialCode));
  },

  /**
   * 9. Get Recent Daily Prices (last 5 days)
   * Endpoint: /materials/{material_code}/recent_daily
   *
   * Returns: Array<{
   *   date: string,
   *   change: number,
   *   price: number
   * }>
   */
  getRecentDaily: async (materialCode) => {
    return apiClient.get(ENDPOINTS.MATERIALS.RECENT_DAILY(materialCode));
  },

  /**
   * 10. Get Rolling Average Series
   * Endpoint: /materials/{material_code}/rolling_average_series
   *
   * Returns: {
   *   days: Array<{ date: string, avg: number | null, count: number }>,
   *   weeks: Array<{ week: string, start: string, end: string, avg: number, count: number }>,
   *   months: Array<{ month: string, avg: number, count: number }>,
   *   years: Array<{ year: string, avg: number, count: number }>,
   *   end_date: string
   * }
   */
  getRollingAverage: async (materialCode) => {
    return apiClient.get(ENDPOINTS.MATERIALS.ROLLING_AVERAGE(materialCode));
  },

  /**
   * Get forecast series
   * Endpoint: /materials/{material_code}/forecast
   *
   * @param {string} materialCode - Material code
   * @param {Object} params - { horizon: '1w'|'1m'|'3m', frequency: 'daily'|'weekly', include_news?: boolean, scenario?: string }
   * @returns {Promise<Object>} - { data: [{ date, value }], horizon_days, frequency }
   */
  getForecast: async (materialCode, params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.MATERIALS.FORECAST(materialCode),
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Get Sector Materials
   * Endpoint: /sectors/{sector_id}/materials
   *
   * @param {number} sectorId - Sector ID
   * @returns {Promise<Object>} - Object with lists array containing materials grouped by list name
   * Returns: {
   *   lists: Array<{
   *     name: string,
   *     materials: Array<{
   *       code: string,
   *       name: string,
   *       last_date: string (ISO format),
   *       last_value: number,
   *       high: number,
   *       low: number,
   *       currency: string,
   *       unit: string,
   *       change_pct: number
   *     }>
   *   }>
   * }
   */
  getSectorMaterials: async (sectorId, params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.SECTORS.MATERIALS(sectorId),
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Fetch multiple materials in parallel (for comparison feature)
   *
   * @param {Array<string>} materialCodes - Array of material codes
   * @param {Function} fetchMethod - Service method to call for each material
   * @returns {Promise<Array>} - Array of results in same order as input
   */
  fetchMultipleMaterials: async (materialCodes, fetchMethod) => {
    try {
      const promises = materialCodes.map(code => fetchMethod(code));
      return await Promise.all(promises);
    } catch (error) {
      console.error('[Material Service] Error fetching multiple materials:', error);
      throw error;
    }
  },

  /**
   * Fetch all data for a single material at once (batch fetch)
   * Useful for initial page load
   *
   * @param {string} materialCode - Material code
   * @returns {Promise<Object>} - Object with all data keyed by endpoint name
   */
  fetchAllData: async (materialCode) => {
    try {
      const [
        general,
        chartOverview,
        assessment,
        availability,
        countryProduction,
        marketStability,
        needProjection,
        recentDaily,
        rollingAverage
      ] = await Promise.all([
        materialService.getGeneral(materialCode),
        materialService.getChartOverview(materialCode),
        materialService.getAssessment(materialCode),
        materialService.getAvailability(materialCode),
        materialService.getCountryProduction(materialCode),
        materialService.getMarketStability(materialCode),
        materialService.getNeedProjection(materialCode),
        materialService.getRecentDaily(materialCode),
        materialService.getRollingAverage(materialCode),
      ]);

      return {
        general,
        chartOverview,
        assessment,
        availability,
        countryProduction,
        marketStability,
        needProjection,
        recentDaily,
        rollingAverage,
      };
    } catch (error) {
      console.error('[Material Service] Error fetching all data:', error);
      throw error;
    }
  },
};

export default materialService;
